import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useTypedContext from "hooks/useTypedContext";
import { pluralizeNext } from "shared/Pluralize";
import useOpenTofuMigrate from "shared/Stack/useOpenTofuMigrate";
import { SearchQueryPredicate, StackVendor } from "types/generated";

import { MIGRATE_REFETCH_QUERIES } from "./constants";
import { OPEN_TOFU_MIGRATION_QUEUE_PATH } from "../constants";

type OpenTofuMigrationMigrateConfirmationModalProps = {
  predicates: SearchQueryPredicate[];
  fullTextSearch: string;
  matchingCount: number;
};

const OpenTofuMigrationMigrateConfirmationModal = create(
  function OpenTofuMigrationMigrateConfirmationModal({
    predicates,
    fullTextSearch,
    matchingCount,
  }: OpenTofuMigrationMigrateConfirmationModalProps) {
    const modal = useModal();
    const navigate = useNavigate();
    const { reportInfo, reportSuccess } = useTypedContext(FlashContext);

    const [migrate, { loading }] = useOpenTofuMigrate({
      refetchQueries: MIGRATE_REFETCH_QUERIES,
    });

    const handleConfirm = useCallback(() => {
      migrate(
        {
          input: {
            predicates,
            fullTextSearch: fullTextSearch || null,
            first: null,
            after: null,
            orderBy: null,
            requestedPage: null,
          },
          target: StackVendor.OpenTofu,
          triggerRun: null,
        },
        (data) => {
          if (data?.openTofuMigrate) {
            const { queued } = data.openTofuMigrate;

            if (queued === 0) {
              reportInfo({
                message:
                  "No stacks were queued. They may already be in the queue or no longer be eligible.",
              });
              modal.resolve();
              modal.hide();

              return;
            }

            reportSuccess({
              message: `${pluralizeNext(queued, "stack")} queued for migration`,
            });
            modal.resolve();
            modal.hide();
            navigate(OPEN_TOFU_MIGRATION_QUEUE_PATH);
          }
        }
      );
    }, [fullTextSearch, migrate, modal, navigate, predicates, reportInfo, reportSuccess]);

    return (
      <ConfirmationModal
        title="Migrate stacks to OpenTofu"
        confirmationButtonLabel="Migrate"
        confirmationButtonVariant="primary"
        onConfirm={handleConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <Box direction="column" gap="medium">
          <ConfirmationModalMessage>
            This queues all {pluralizeNext(matchingCount, "stack")} matching the current filters and
            search, not just the ones on this page.
          </ConfirmationModalMessage>
          <ConfirmationModalMessage>
            Each stack keeps its configuration and state, and you can roll the migration back.
          </ConfirmationModalMessage>
        </Box>
      </ConfirmationModal>
    );
  }
);

export const showOpenTofuMigrationMigrateConfirmationModal = (
  props: OpenTofuMigrationMigrateConfirmationModalProps
) => show(OpenTofuMigrationMigrateConfirmationModal, props);
